import { Component, ComponentFactoryResolver, forwardRef, OnDestroy, OnInit, ViewChild, ViewContainerRef, NgZone } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { assign, flatten, keyBy } from 'lodash';
import { CoordinateBoundary, PointGeometry, Marker, Polygon, MOUSE_EVENT_TYPE } from '@realpage-da/ng-map';

import { ViewComponent } from './view.component';
import { VIEW_LEVELS, MARKET_BOUNDARY_STYLE, SUBMARKET_BOUNDARY_STYLE } from '../constants';

import { MarketService, PropertyService, AnalyticsService, DecryptionService } from '~/core/services';
import { MarketBounds, PropertyInfo } from '~/core/model';
import { PropertyMetric } from '~/core/model/PropertyMetric';
import { PropertyLegendComponent } from '~/features/property/components/map';
import { PropertyMarkerComponent, PropertyTooltipComponent } from '~/features/property/components/map';
import { SIZES } from '~/shared/configs';
import { PropertyTableComponent } from '~/features/property/components/tables';
import { REGION_TYPES, RegionBoundary } from '~core/model';
import { FilterFunction } from '~shared/components/filters/components/controls';
import * as CryptoJS from 'crypto-js';
import { PropertyDetailPopupAnchorDirective, PropertyDetailPopupComponent } from '~/features/property/components/popup';
import { LeaderboardComponent, LeaderboardAnchorDirective } from '.';

type SortFunction<T> = (l: T, r: T) => number;

interface ViewState {
  properties: PropertyMetric[];
  sort: {
    comparator: SortFunction<PropertyMetric>;
    column: string;
    direction: 'asc' | 'desc';
  };

  bounds: CoordinateBoundary;

  params?: {
    marketId: number;
  }
}

const updateSort = (column: string, direction: 'asc' | 'desc') => (state: ViewState) => assign({}, state, {
  sort: {
    column,
    direction,
    comparator: (l, r) => l[column] - r[column]
  }
});

const updateProperties = (properties: PropertyMetric[]) => (state: ViewState) => assign({}, state, { properties });
const updateBounds = (bounds: CoordinateBoundary) => (state: ViewState) => assign({}, state, { bounds });

@Component({
  selector: 'idap-property-view',
  templateUrl: './property-view.component.html',
  styleUrls: ['./property-view.component.scss']
})
export class PropertyViewComponent extends ViewComponent implements OnInit, OnDestroy {
  @ViewChild(PropertyDetailPopupAnchorDirective) propertyDetailPopupAnchor: PropertyDetailPopupAnchorDirective;
  @ViewChild(PropertyTableComponent) private table: PropertyTableComponent;
  @ViewChild(forwardRef(() => LeaderboardAnchorDirective))
  private leaderboardAnchor: LeaderboardAnchorDirective;

  onFilterChanged: Subject<FilterFunction<any>> = new Subject();

  static viewLevel = VIEW_LEVELS.PROPERTY;

  marketBounds: Observable<MarketBounds[]>;
  properties: Observable<PropertyMetric[]>;
  markets: Observable<MarketBounds[]>;
  filteredProperties: Observable<PropertyMetric[]>;
  marketImages: Observable<PropertyInfo[]>;

  maxWidth: number = SIZES.characterWidth(197);
  // the passed value here is based on the total
  // characters width we have defined for all the
  // columns in the property level grid
  halfWidth: number = SIZES.characterWidth(77.5);

  markers: Observable<Marker[]>;

  constructor(
    private propertyService: PropertyService,
    private marketService: MarketService,
    private ngZone: NgZone,
    public analytics: AnalyticsService,
    public decryptionService: DecryptionService) {
    super();

    this.marketBounds = this.marketService
      .getMarketBounds()
      .takeUntil(this.onDestroy)
      .share();
  }

  ngOnInit() {
    // const marketId = this.state && this.state.params && this.state.params.marketId;z

    this.map.map.setMarkers([]);
    this.map.map.addLegend(PropertyLegendComponent);

    const markets = this.state
      .filter(state => state.sidebar !== 'full')
      .map((state) => state.bounds)
      .distinctUntilChanged()
      .startWith(this.map.getBounds())
      .combineLatest(this.marketBounds)
      .map(([bounds, mkts]) => mkts.filter(f => bounds.intersects(f.geometry)))
      .distinctUntilChanged()
      .takeUntil(this.onDestroy)

    const properties = markets
      .switchMap(mkts => Observable.combineLatest(mkts
        .map(market => {
          return this.propertyService.getMarketProperties(market.properties.id)
            .map(props => {
              return props.map(p => {
                p.marketName = market.properties.name;
                return p;
              })
            })
        })))
      .map(flatten);

    this.marketImages = this.marketService.getMarketImage()
      .takeUntil(this.onDestroy)
      .switchMap(data => {
        const key = 'bgdi8144Kwp09773';
        return this.decryptionService.decryptAesGcm(data.response, key);
      })
      .map(finalJson => JSON.parse(finalJson) as PropertyInfo[])
    
    const updatedProperties = Observable.combineLatest(properties, this.marketImages)
      .map(([properties, tempProperties]) => {
        const propertyMap = keyBy(tempProperties, 'propertyId');
        properties.forEach(a => {
          if (propertyMap[a.propertyId]) {
            a.propertyName = propertyMap[a.propertyId].propertyName;
          }
        });
        return properties;
      });

    const boundaries: Observable<RegionBoundary[]> = markets
      .switchMap(markets => Observable.combineLatest(markets
        .map(market => this.marketService.getRegionsByMarket(market.properties.id)))
      )
      .map(flatten)
      .share()

    const polygons = boundaries
      .map(boundaries => boundaries
        .map(boundary => {
          if (boundary.region.regionType === REGION_TYPES.MARKET) {
            return <Polygon>({
              id: boundary.region.regionCode,
              geometry: boundary.geometry,
              options: MARKET_BOUNDARY_STYLE,
              data: boundary.region
            })
          } else if (boundary.region.regionType === REGION_TYPES.SUBMARKET) {
            return <Polygon>({
              id: boundary.region.regionCode,
              geometry: boundary.geometry,
              options: SUBMARKET_BOUNDARY_STYLE,
              data: boundary.region
            })
          }
        })
        .sort((l, r) => !!l && !!r && r.data.regionType.localeCompare(l.data.regionType))
      );

    polygons
      .takeUntil(this.onDestroy)
      .subscribe(polygons => this.map.map.setPolygons(polygons));

    this.properties = Observable.merge(
      updatedProperties.map(updateProperties),
      this.map.map.boundsChanged
        .combineLatest(this.state)
        .filter(([bounds, state]) => state.sidebar !== 'full')
        .map(([bounds]) => bounds)
        .startWith(this.map.getBounds())
        .map(updateBounds)
    )
      .scan((state, reduce) => reduce(state), {} as any as ViewState)
      .share()
      .map((state: ViewState) => {
        return (state && state.properties || [])
          .sort((l, r) => !!l && !!r && l.propertyName.localeCompare(r.propertyName))
          .filter(f => !state.bounds || state.bounds.contains(f.coordinate))
        // .slice(0, 750) -- Commenting this code for Business to do some testing.
      });

    const mouseEvent = this.map.map.onMarkerSelected
      .takeUntil(this.onDestroy)
      .share();

    mouseEvent
      .filter(e => e.event === MOUSE_EVENT_TYPE.CLICK)
      .subscribe((clickEvent) => {
        this.map.map.addTooltip(clickEvent.marker);
        this.table.selectRow(clickEvent.marker.id);
        this.analytics.emitEvent('click', {
          event_category: 'property',
          event_label: 'map-marker-click',
          value: clickEvent.marker.id
        });
      });

    this.parent.searchResponse
      .delayWhen(() => this.table.propertyDataReady
        .filter(f => (f))
        .takeUntil(this.onDestroy))
      .takeUntil(this.onDestroy)
      .debounceTime(1000)
      .subscribe(marker => {
        this.map.map.addTooltip(marker);
        this.table.selectRow(marker.id);
      });

    this.parent.openPropertyModal
      .takeUntil(this.onDestroy)
      .subscribe(property => {
        this.showPropertyProfilePopup(property)
      });

    const markers = this.properties
      .map(properties => properties.map(p => ({
        id: p.id,
        geometry: new PointGeometry(+p.latitude, +p.longitude),
        options: {
          icon: { component: PropertyMarkerComponent },
          tooltip: PropertyTooltipComponent
        },
        data: p
      })))
      .takeUntil(this.onDestroy)

    this.filteredProperties = Observable.combineLatest(this.properties, this.onFilterChanged)
      .map(([props, filterFn]) => props.filter(f => (filterFn(f))));

    this.markers = Observable.combineLatest(this.filteredProperties, markers)
      .map(([properties, markers]) => {
        const propertyMap = keyBy(properties, p => p.id);
        return markers.filter(f => !!propertyMap[f.id]);
      });

    this.markers
      .takeUntil(this.onDestroy)
      .subscribe(markers => this.map.map.setMarkers(markers));
  }

  propertySelected(data) {
    this.map.map.addTooltip(this.map.map.findFeature(data.id));
    this.analytics.emitEvent('click', {
      event_category: 'property',
      event_label: 'grid-row-click',
      value: data.id
    });
  }

  showPropertyProfilePopup(property) {
    this.ngZone.run(() => {
      this.propertyDetailPopupAnchor.createPropertyDetailPopup(PropertyDetailPopupComponent, property);
    });
  }

  showLeaderboardPopup(marketId: number) {
    this.ngZone.run(() => {
      this.leaderboardAnchor.createLeaderboardModalPopup(LeaderboardComponent, marketId)
    });
  }
  ngOnDestroy() {
    // unset(this.state && this.state.params, 'marketId');
    this.map.map.removeLegend();
    this.onFilterChanged.complete();
    return super.ngOnDestroy();
  }
}



// WEBPACK FOOTER //
// ./src/app/main/views/property-view.component.ts